import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";

import {
  createClientCharacteristic,
  updateClientCharacteristic,
} from "actions/clientCharacteristics";
import type { ClientCharacteristic, CreateCharacteristicPayload, UpdateCharacteristicPayload } from "types/domain";

interface CharacteristicModalProps {
  open: boolean;
  /** null = Add mode, provided = Edit mode */
  characteristic: ClientCharacteristic | null;
  onClose: (shouldRefetch: boolean) => void;
  /** Called when delete button is clicked in edit mode (parent opens DeleteConfirmationModal) */
  onDelete?: () => void;
}

/**
 * Modal for creating or editing a client characteristic.
 * Manages form state, validation, and async save.
 *
 * State machine:
 *  S-1: Add modal open (defaults)
 *  S-2: Saving (spinner, disabled inputs)
 *  S-3: Success (close modal, toast)
 *  S-4: Validation error (inline error, modal stays open)
 *  S-5: Server error (toast, modal stays open)
 *  S-6: Edit modal open (pre-populated)
 */
export const CharacteristicModal: React.FC<CharacteristicModalProps> = ({
  open,
  characteristic,
  onClose,
  onDelete,
}) => {
  const isEditMode = characteristic !== null;
  const isBase = characteristic?.is_base ?? false;

  // ── Form state ──────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [lockedToEdit, setLockedToEdit] = useState(false);
  const [includeInAi, setIncludeInAi] = useState(false);
  const [isGlobal, setIsGlobal] = useState(true);
  const [show, setShow] = useState(false);
  const [multiple, setMultiple] = useState(false);
  const [numerify, setNumerify] = useState<string | null>(null);
  const [postfix, setPostfix] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens or characteristic changes
  useEffect(() => {
    if (characteristic) {
      // S-6: Edit modal — pre-populate
      setName(characteristic.name);
      setLockedToEdit(characteristic.locked_to_edit);
      setIncludeInAi(characteristic.include_in_ai);
      setIsGlobal(characteristic.is_global);
      setShow(characteristic.show);
      setMultiple(characteristic.multiple);
      setNumerify(characteristic.numerify);
      setPostfix(characteristic.postfix);
    } else {
      // S-1: Add modal — defaults
      setName("");
      setLockedToEdit(false);
      setIncludeInAi(false);
      setIsGlobal(true);
      setShow(false);
      setMultiple(false);
      setNumerify(null);
      setPostfix(null);
    }
    setFieldErrors({});
    setSaving(false);
  }, [characteristic, open]);

  // ── Client-side validation ──────────────────────────────────────────
  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    // Only block truly empty names; whitespace-only is handled by the API
    if (!name || name.length === 0) {
      errors.name = "Name is required";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Save handler ────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;

    // S-2: Saving
    setSaving(true);
    setFieldErrors({});

    const payload: CreateCharacteristicPayload | UpdateCharacteristicPayload = {
      name: name.trim(),
      locked_to_edit: lockedToEdit,
      include_in_ai: includeInAi,
      is_global: isGlobal,
      show: show,
      multiple: multiple,
      numerify: numerify,
      postfix: postfix,
    };

    try {
      if (isEditMode) {
        await updateClientCharacteristic(characteristic.id, payload as UpdateCharacteristicPayload);
      } else {
        await createClientCharacteristic(payload as CreateCharacteristicPayload);
      }
      // S-3: Success
      toast.success("Characteristic saved");
      onClose(true);
    } catch (error: any) {
      if (error?.response?.status === 422) {
        // S-4: Validation error from backend
        const serverErrors = error?.response?.data?.errors || {};
        const mappedErrors: Record<string, string> = {};
        Object.entries(serverErrors).forEach(([field, messages]) => {
          mappedErrors[field] = Array.isArray(messages) ? messages[0] : String(messages);
        });
        setFieldErrors(mappedErrors);
      } else {
        // S-5: Server error
        toast.error("Something went wrong. Please try again.");
      }
      setSaving(false);
    }
  };

  // ── Cancel handler ──────────────────────────────────────────────────
  const handleCancel = () => {
    if (!saving) {
      onClose(false);
    }
  };

  // ── Numerify / Postfix options ──────────────────────────────────────
  const numerifyOptions = [
    { value: "", label: "Select…" },
    { value: "number", label: "Number" },
    { value: "percentage", label: "Percentage" },
    { value: "currency", label: "Currency" },
  ];

  const postfixOptions = [
    { value: "", label: "Select…" },
    { value: "%", label: "%" },
    { value: "$", label: "$" },
    { value: "€", label: "€" },
    { value: "pts", label: "pts" },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      data-testid="characteristic-modal"
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={saving}
    >
      <DialogTitle>
        {isEditMode ? `Edit ${characteristic.name}` : "Add Characteristic"}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Name field */}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || ""}
            fullWidth
            required
            slotProps={{
              htmlInput: {
                "data-testid": "name-input",
              } as any,
            }}
          />

          {/* Locked to edit */}
          <FormControlLabel
            control={
              <Checkbox
                checked={lockedToEdit}
                onChange={(e) => setLockedToEdit(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { "data-testid": "locked-to-edit-checkbox" } as any }}
              />
            }
            label="Locked to edit"
          />

          {/* Include in AI processing */}
          <FormControlLabel
            control={
              <Checkbox
                checked={includeInAi}
                onChange={(e) => setIncludeInAi(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { "data-testid": "include-in-ai-checkbox" } as any }}
              />
            }
            label="Include in AI processing"
          />

          {/* Is Global */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { "data-testid": "is-global-checkbox" } as any }}
              />
            }
            label="Is Global"
          />

          {/* Show */}
          <FormControlLabel
            control={
              <Checkbox
                checked={show}
                onChange={(e) => setShow(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { "data-testid": "show-checkbox" } as any }}
              />
            }
            label="Show"
          />

          {/* Multiple */}
          <FormControlLabel
            control={
              <Checkbox
                checked={multiple}
                onChange={(e) => setMultiple(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { "data-testid": "multiple-checkbox" } as any }}
              />
            }
            label="Multiple"
          />

          {/* Numerify */}
          <TextField
            select
            label="Numerify"
            value={numerify ?? ""}
            onChange={(e) => setNumerify(e.target.value || null)}
            disabled={saving}
            fullWidth
          >
            {numerifyOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Postfix */}
          <TextField
            select
            label="Postfix"
            value={postfix ?? ""}
            onChange={(e) => setPostfix(e.target.value || null)}
            disabled={saving}
            fullWidth
          >
            {postfixOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Box>
          {/* Delete button — only in Edit mode for non-base characteristics */}
          {isEditMode && !isBase ? (
            <Button
              data-testid="delete-button"
              onClick={onDelete}
              disabled={saving}
              color="error"
              variant="outlined"
            >
              Delete
            </Button>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleCancel}
            disabled={saving}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            data-testid="save-button"
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            color="primary"
          >
            {saving ? (
              <CircularProgress
                data-testid="save-spinner"
                size={20}
                color="inherit"
              />
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CharacteristicModal;
